<template>
  <div
    ref="filterDateRef"
    class="filter-date-wrapper filter-item"
  >
    <div class="filter-datepicker">
      <div class="flex justify-space-between title-container mb-md align-center">
        <h4 class="intro-text">Datum</h4>
        <button
          v-if="initialDateValue && initialDateValue[1]"
          class="filter-delete m-0 flex align-center flex-gap-8 flex-nowrap"
          @click="deleteDateFilter()"
        >
          <UIIconComponent icon="close" />
          Verwijder filter
        </button>
      </div>

      <UIHoverAnimationButton
        v-if="!props.modalAlwaysOpen"
        class="btn btn-tertiary"
        name="Selecteer datum"
        @click="showDatepicker = !showDatepicker"
      />

      <VueDatePicker
        v-if="showDatepicker"
        v-model="internalDate"
        inline
        class="datepicker"
        :enable-time-picker="false"
        cancel-text="Annuleren"
        select-text="Selecteren"
        :locale="dateLocale"
        :min-date="new Date()"
        :time-config="{ enableTimePicker: false }"
        :month-change-on-scroll="false"
        auto-apply
        :range="false"
      />
    </div>
  </div>
</template>
<script setup lang="ts">
  import { VueDatePicker } from '@vuepic/vue-datepicker'
  import { enUS, nl } from 'date-fns/locale'

  const props = defineProps<{
    initialDateValue?: Date[]
    modalAlwaysOpen?: boolean
  }>()

  const showDatepicker = ref(props.modalAlwaysOpen ?? false)
  const filterDateRef = ref<HTMLElement | null>(null)
  const internalDate = ref<Date | null>(null)

  const localization = useBaseLocalization()
  const currentLanguage = computed(() => localization.getCurrentLanguage())
  const dateLocale = computed(() => {
    if (currentLanguage.value.name == 'en') {
      return enUS
    }
    return nl
  })

  const emit = defineEmits<{
    (e: 'update-date', startDate: Date | null, endDate: Date | null): void
  }>()

  const dateOptions = (() => {
    const now = new Date()
    const dayOfWeek = now.getDay() // 0 = Sunday, 5 = Friday, 6 = Saturday
    const daysUntilFriday = dayOfWeek === 0 ? 5 : dayOfWeek <= 5 ? 5 - dayOfWeek : 6 // next friday
    const friday = new Date(now)
    friday.setDate(now.getDate() + daysUntilFriday)
    friday.setHours(12, 0, 0, 0)
    const sunday = new Date(friday)
    sunday.setDate(friday.getDate() + 2)
    sunday.setHours(23, 59, 59, 999)
    return [
      {
        label: 'Komend weekend',
        value: [friday, sunday] as [Date, Date],
      },
    ]
  })()

  function handleDateOptionChange(optionValue: [Date, Date]) {
    if (isDateOptionSelected(optionValue)) {
      internalDate.value = null
      emit('update-date', null, null)
    } else {
      internalDate.value = null
      emit('update-date', optionValue[0], optionValue[1])
    }
  }

  function isDateOptionSelected(optionValue: [Date, Date]) {
    const end = props.initialDateValue?.[1]
    if (!end) return false

    const formatDate = (date: Date) => [date.getDate(), date.getMonth()].join('-')
    const selectedString = formatDate(new Date(end))
    const optionValueEndString = formatDate(optionValue[1])

    return selectedString === optionValueEndString
  }

  async function deleteDateFilter() {
    internalDate.value = null
    showDatepicker.value = false

    emit('update-date', null, null)
  }

  let stopInternalDateWatch: (() => void) | null = null

  function handleOutsideClick(event: MouseEvent) {
    const target = event.target as Node | null
    if (!target || !filterDateRef.value || props.modalAlwaysOpen) return

    if (showDatepicker.value && !filterDateRef.value.contains(target)) {
      showDatepicker.value = false
    }
  }

  onMounted(async () => {
    stopInternalDateWatch = watch(internalDate, (newDate) => {
      if (newDate) {
        emit('update-date', newDate, newDate)
        if (!props.modalAlwaysOpen) showDatepicker.value = false
      }
    })

    document.addEventListener('click', handleOutsideClick)

    await import('@vuepic/vue-datepicker/dist/main.css')
    await import('@/assets/styles/datepicker.scss')
  })

  onUnmounted(() => {
    if (stopInternalDateWatch) stopInternalDateWatch()
    document.removeEventListener('click', handleOutsideClick)
  })
</script>
